<template>
  <content-subpage
    :visible="visible"
    :titles="titles"
    class="announcement-edit-content-subpage"
    @content-subpage-hidden="actionHidden"
  >
    <template v-if="showHeaderButton" #header-buttons>
      <fetch-data-button
        :kind="dataSaveButton.kind"
        :size="dataSaveButton.size"
        :error="dataSaveButton.error"
        :loading="dataSaveButton.loading"
        :icon-only="dataSaveButton.iconOnly"
        :tip-position="dataSaveButton.tipPosition"
        :error-message="dataSaveButton.errorMessage"
        :completed="dataSaveButton.completed"
        @click="handleUpdate"
        @reset-error-state="handleSaveButtonResetError"
        @reset-completed-state="handleSaveButtonResetCompleted"
      >
        <svg-icon :name="dataSaveButton.iconName" kind="action" />
      </fetch-data-button>
    </template>
    <template #main-content>
      <div class="announcement-editor" :class="{ edit: isEdit }">
        <overlay-loader v-if="isLoading" />
        <template v-else>
          <div v-if="isEdit" class="announcement-actions">
            <overflow-menu :flip-menu="true">
              <template #trigger>
                <svg-icon name="Kebab24" kind="action" />
              </template>
              <OverflowMenuItem @click="handleDelete">
                <svg-icon name="Pencil24" kind="action" />
                {{ t("button_delete") }}
              </OverflowMenuItem>
            </overflow-menu>
          </div>
          <div class="announcement-editor-content">
            <cv-form class="announcement">
              <div class="field-group sender-receiver">
                <dl class="field-set receiver">
                  <dt class="field-label">
                    {{ t("announcement_receiver_receiver_selection_label") }}
                  </dt>
                  <dd v-if="isEdit" class="field-value">{{ notificationReceiver }}</dd>
                  <dd v-else class="field-input">
                    <dropdown-component
                      kind="white"
                      :value="audienceTypeSelected"
                      @change="handleAudienceTypeChange"
                    >
                      <cv-dropdown-item
                        v-for="(type, index) in audienceTypes"
                        :key="index"
                        :value="type"
                      >
                        {{ getAudienceTypeLabel(type) }}
                      </cv-dropdown-item>
                    </dropdown-component>
                  </dd>
                </dl>
                <dl v-if="isEdit" class="field-set owner">
                  <dt class="field-label">
                    {{ t("announcement_owner_form_label") }}
                  </dt>
                  <dd class="field-value">
                    {{ notification.createdByName }}
                  </dd>
                </dl>
              </div>
              <dl class="field-set title">
                <dt class="field-label">{{ t("announcement_title_form_label") }}</dt>
                <dd class="field-input">
                  <text-input
                    :value="formData.title"
                    :mandatory="true"
                    placeholder="Title"
                    :character-limit="titleCharacterLimit"
                    :error-message="error.title"
                    :text-id="dirtyId"
                    @input="handleTitleChange"
                  />
                </dd>
              </dl>
              <dl class="field-set message">
                <dt class="field-label">{{ t("message_form_label") }}</dt>
                <dd class="field-input">
                  <rich-text-editor
                    ref="richTextEditor"
                    kind="teacher_tinymce_4"
                    :initial-content="formData.content"
                    size="large"
                    :is-mandatory="true"
                    @change="handleMessageChange"
                  />
                </dd>
              </dl>
              <div class="field-group schedule">
                <!-- Start Date Time -->
                <dl class="field-set start-date">
                  <dd class="field-input">
                    <DatePicker
                      class="date-picker"
                      kind="single"
                      :date-label="t('announcement_start_date_form_label')"
                      :value="formData.startDate"
                      :cal-options="calendarOption"
                      :invalid-message="error.startDate"
                      placeholder="dd Mmm yyyy"
                      @change="onStartDateChange"
                    />
                    <text-input
                      class="time-picker"
                      :label="t('announcement_start_time_form_label')"
                      :input-type="'time'"
                      :value="formData.startTime"
                      :error-message="error.startTime"
                      :text-id="dirtyId"
                      @change="onUpdateStartTime"
                    />
                  </dd>
                </dl>
              </div>
            </cv-form>
          </div>
        </template>
      </div>
    </template>
    <template v-if="canSubmit" #action-button>
      <fetch-data-button
        :kind="dataSaveButton.kind"
        :error="dataSaveButton.error"
        :loading="dataSaveButton.loading"
        :error-message="dataSaveButton.errorMessage"
        tip-position="top"
        tip-alignment="end"
        @click="handleSend"
        @reset-error-state="handleSaveButtonResetError"
        @reset-completed-state="handleSaveButtonResetCompleted"
      >
        <template v-if="dataSaveButton.completed">
          {{ t("button_state_saved") }}
        </template>
        <template v-else-if="dataSaveButton.error">
          {{ t("button_state_error") }}
        </template>
        <template v-else>
          <svg-icon name="Plus24" kind="action" />
          {{ t("button_create") }}
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm";
import {
  ContentSubpageParams,
  useContentSubpage,
} from "@plugins/content-subpage/composables/content-subpage.ts";
import NotificationEditContentSubpageMixin from "@modules/notification/mixin/notification.edit.content.subpage.mixin";
import LockService from "@plugins/common/service/lock.service";
import { LOCKABLE_RESOURCE_TYPE } from "@plugins/common/service/lock.constant";
import {
  NOTIFICATION_AUDIENCE,
  SYSTEM_NOTIFICATION_AUDIENCE_OPTIONS,
  SYSTEM_NOTIFICATION_AUDIENCE_OPTIONS_LABEL,
} from "@modules/notification/service/notification.constant";
import NotificationUtils from "@modules/notification/service/notification.utils";

// defineOptions
defineOptions({
  mixins: [NotificationEditContentSubpageMixin],
});

// Props
interface SystemNotificationEditContentSubpageParam extends ContentSubpageParams {
  kind?: string;
  data?: Record<string, unknown>;
}
interface Props {
  params?: SystemNotificationEditContentSubpageParam | null;
}
const props = withDefaults(defineProps<Props>(), {
  params: null,
});

// Composables
const { t } = useI18n();
const { vm, vmExpose } = useVm();

const id = props.params.id;
const dirtyId = ref(id);

const {
  titles,
  visible,
  dataSaveButton,
  handleSaveButtonResetError,
  handleSaveButtonResetCompleted,
  actionHidden,
  defaultExpose,
  setDataState,
  setErrorMessage,
  setDirty,
  removeDirty,
} = useContentSubpage(props.params);

// Data
const audienceTypes = ref(SYSTEM_NOTIFICATION_AUDIENCE_OPTIONS);
const audienceTypeSelected = ref("");

// Computed
const notificationReceiver = computed(() =>
  NotificationUtils.getNotificationGroupReceiver(vm.notification),
);

// Methods
function validate() {
  let isValid = vm.validateBasicFields();
  for (const key in vm.error) {
    if (vm.error[key] !== "") {
      isValid = false;
    }
  }
  return isValid;
}

function getRequestData() {
  const requestData = {
    title: vm.formData.title,
    rt_content: vm.formData.content,
    startDateTime: vm.formData.startDateTime,
    notify: vm.formData.notify,
    scope: vm.scope,
  };
  if (vm.isEdit) {
    requestData.uuid = vm.notification.uuid;
  }
  requestData.audienceTypes = [audienceTypeSelected.value];
  return requestData;
}

function handleAudienceTypeChange(audienceType: string) {
  if (audienceTypeSelected.value === audienceType) return;
  audienceTypeSelected.value = audienceType;
  setDirty(dirtyId.value);
}

function getAudienceTypeLabel(type: string) {
  return t(SYSTEM_NOTIFICATION_AUDIENCE_OPTIONS_LABEL[type]);
}

function populateForm() {
  vm.populateCommonFields();
}

// Lifecycle
onMounted(() => {
  if (vm.isEdit) {
    audienceTypeSelected.value = vm.notification.notificationRecipients[0].audienceType;
    vm.lockService = new LockService(
      LOCKABLE_RESOURCE_TYPE.NOTIFICATION,
      vm.notification.uuid,
      vm.handleLockRetrievalFail,
    );
    vm.lockService.startLockInterval().then(
      () => {
        populateForm();
        vm.checkAndUpdateStartDateValue();
        vm.isLoading = false;
      },
      () => {
        /* Empty Function */
      },
    );
  } else {
    audienceTypeSelected.value = NOTIFICATION_AUDIENCE.ALL;
    populateForm();
    vm.isLoading = false;
  }
});

onBeforeUnmount(() => {
  if (vm.lockService) {
    vm.lockService.destroy();
    delete vm.lockService;
  }
});

defineExpose(defaultExpose);

vmExpose({
  id,
  actionHidden,
  setDirty,
  removeDirty,
  setDataState,
  setErrorMessage,
  validate,
  getRequestData,
});
</script>
